import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { ChevronLeft } from 'lucide-react';
import { setPageTitle } from '../utils/pageTitle';
import { privacyPolicyContent } from '../data/privacyPolicyContent';

// CSS for the privacy policy page
const styles = {
  section: "mb-12 pb-8 border-b border-gray-200 dark:border-gray-700 last:border-0",
  sectionTitle: "text-2xl font-bold mb-6 text-gray-900 dark:text-white pb-2 border-b border-gray-200 dark:border-gray-700",
  subheading: "text-xl font-semibold mt-8 mb-4 text-gray-800 dark:text-gray-100",
  paragraph: "text-gray-700 dark:text-gray-300 mb-4 leading-relaxed",
  list: "list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-6 ml-4",
  serviceBlock: "bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-6 border-l-4 border-blue-500 dark:border-blue-400",
  serviceTitle: "font-semibold text-gray-800 dark:text-gray-100 mb-1",
  serviceDetail: "text-gray-600 dark:text-gray-400 text-sm mb-1",
  link: "text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline",
  tocButton: "text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline transition-colors duration-200 block py-1.5",
  tocContainer: "mb-8 p-5 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700",
  ownerCard: "mb-8 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow-sm border border-blue-200 dark:border-blue-800",
};

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    setPageTitle(privacyPolicyContent.title);
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // Function to handle anchor links
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Function to process HTML content and add custom styling
  const processContent = (content: string) => {
    // Replace HTML tags with styled versions
    let processedContent = content
      .replace(/<h3>/g, `<h3 class="${styles.subheading.replace(/ /g, ' ')}">`)
      .replace(/<h4>/g, `<h4 class="${styles.serviceTitle.replace(/ /g, ' ')}">`)
      .replace(/<p>/g, `<p class="${styles.paragraph.replace(/ /g, ' ')}">`)
      .replace(/<ul>/g, `<ul class="${styles.list.replace(/ /g, ' ')}">`)
      .replace(/<div>/g, `<div class="${styles.serviceBlock.replace(/ /g, ' ')}">`)
      .replace(/<a /g, `<a class="${styles.link.replace(/ /g, ' ')}" `);
    
    return processedContent;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <div className="mb-6">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
        </div>

        <Card className="p-6 md:p-8 shadow-lg">
          {/* Header */}
          <div className="mb-10 pb-6 border-b dark:border-gray-700">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{privacyPolicyContent.title}</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4 text-lg">
              {privacyPolicyContent.intro}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Latest update: {privacyPolicyContent.lastUpdate}
            </p>
          </div>

          {/* Table of Contents */}
          <div className={styles.tocContainer}>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Table of Contents</h2>
            <ul className="space-y-1">
              {privacyPolicyContent.sections.map((section) => (
                <li key={section.id}>
                  <button 
                    onClick={() => scrollToSection(section.id)}
                    className={styles.tocButton}
                  >
                    {section.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Owner and Data Controller */}
          <div className={styles.ownerCard}>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Owner and Data Controller</h2>
            <div className="text-gray-700 dark:text-gray-300">
              <p className="font-medium text-lg">{privacyPolicyContent.ownerInfo.name}</p>
              <p>{privacyPolicyContent.ownerInfo.address}</p>
              <p>{privacyPolicyContent.ownerInfo.city}</p>
              <p className="mt-3">
                <span className="font-medium">Owner contact email:</span>{' '}
                <a 
                  href={`mailto:${privacyPolicyContent.ownerInfo.email}`} 
                  className={styles.link}
                >
                  {privacyPolicyContent.ownerInfo.email}
                </a>
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div>
            {privacyPolicyContent.sections.map((section) => (
              <section key={section.id} id={section.id} className={styles.section}>
                <h2 className={styles.sectionTitle}>{section.title}</h2>
                {section.id === 'summary' ? (
                  <p className={styles.paragraph}>{section.content}</p>
                ) : (
                  <div 
                    className="text-gray-700 dark:text-gray-300"
                    dangerouslySetInnerHTML={{ __html: processContent(section.content) }}
                  />
                )}
              </section>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              © {new Date().getFullYear()} {privacyPolicyContent.ownerInfo.name}. All rights reserved.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
